import type { Dirent } from "node:fs";
import fs, { type FileHandle } from "node:fs/promises";
import path from "node:path";
import {
  type DirEntry,
  type FileSystemPort,
  type FileWithStat,
  PathNotFoundError,
  type PathStat,
  toSafeSegments,
} from "../ports.js";

function isNotFound(err: unknown): boolean {
  return (err as NodeJS.ErrnoException).code === "ENOENT";
}

/**
 * A `FileSystemPort` over a real directory on disk — what the Zuplo CLI runs
 * against.
 *
 * All the `node:fs` specifics the routes used to carry live here: joining onto
 * the project root, the open-file-handle dance behind `readFileWithStat`, and
 * translating `ENOENT` into `PathNotFoundError`.
 */
export function createNodeFileSystem(sourceDirectory: string): FileSystemPort {
  /**
   * Resolves a project-relative path to an absolute one.
   *
   * `toSafeSegments` has already refused anything containing `..`, so joining
   * the segments can't escape `sourceDirectory` — the segments are rebuilt from
   * the validated list rather than from the caller's string so that guarantee
   * can't be sidestepped by a separator the split didn't expect.
   */
  const resolve = (relativePath: string): string =>
    path.join(sourceDirectory, ...toSafeSegments(relativePath));

  return {
    async readFileWithStat(relativePath: string): Promise<FileWithStat> {
      const fullPath = resolve(relativePath);

      let handle: FileHandle;
      try {
        handle = await fs.open(fullPath, "r");
      } catch (err) {
        if (isNotFound(err)) {
          throw new PathNotFoundError(relativePath);
        }
        throw err;
      }

      try {
        // Both reads come from the same open handle, so the mtime returned
        // can't describe a different version of the file than the content it
        // accompanies. See `FileSystemPort.readFileWithStat`.
        const stats = await handle.stat();
        const content = await handle.readFile("utf-8");
        return { content, mtime: stats.mtime };
      } finally {
        await handle.close();
      }
    },

    async listDir(relativePath: string): Promise<DirEntry[]> {
      const fullPath = resolve(relativePath);

      let entries: Dirent[];
      try {
        entries = await fs.readdir(fullPath, { withFileTypes: true });
      } catch (err) {
        if (isNotFound(err)) {
          throw new PathNotFoundError(relativePath);
        }
        throw err;
      }

      // Anything that is neither a file nor a directory (sockets, devices, a
      // symlink to nowhere) is dropped rather than guessed at — the editor has
      // no way to display it.
      return entries.flatMap((entry): DirEntry[] => {
        if (entry.isDirectory()) {
          return [{ name: entry.name, type: "directory" }];
        }
        if (entry.isFile()) {
          return [{ name: entry.name, type: "file" }];
        }
        return [];
      });
    },

    async statPath(relativePath: string): Promise<PathStat | undefined> {
      try {
        const stats = await fs.stat(resolve(relativePath));
        if (stats.isDirectory()) {
          return { type: "directory", mtime: stats.mtime };
        }
        if (stats.isFile()) {
          return { type: "file", mtime: stats.mtime };
        }
        return undefined;
      } catch (err) {
        if (isNotFound(err)) {
          return undefined;
        }
        throw err;
      }
    },

    async writeFile(
      relativePath: string,
      content: string | Uint8Array,
    ): Promise<PathStat> {
      const fullPath = resolve(relativePath);

      await fs.mkdir(path.dirname(fullPath), { recursive: true });
      await fs.writeFile(fullPath, content);

      const stats = await fs.stat(fullPath);
      return { type: "file", mtime: stats.mtime };
    },

    async rename(from: string, to: string): Promise<void> {
      const fromPath = resolve(from);
      const toPath = resolve(to);

      // Renaming into a directory that doesn't exist yet is how the portal
      // moves a file into a new folder, so create the destination's parents
      // rather than failing.
      await fs.mkdir(path.dirname(toPath), { recursive: true });

      try {
        await fs.rename(fromPath, toPath);
      } catch (err) {
        if (isNotFound(err)) {
          throw new PathNotFoundError(from);
        }
        throw err;
      }
    },

    async remove(relativePath: string): Promise<void> {
      try {
        await fs.unlink(resolve(relativePath));
      } catch (err) {
        if (isNotFound(err)) {
          throw new PathNotFoundError(relativePath);
        }
        throw err;
      }
    },

    async mkdirp(relativePath: string): Promise<void> {
      await fs.mkdir(resolve(relativePath), { recursive: true });
    },
  };
}
